import { Utility } from ".";
import * as moment from "moment";
import sentenceCase from "sentence-case";
import * as yup from "yup";
import { ReactSelectOption } from "../models/generic";

/**
 * Validation method that checks for equality of current field to another field
 * @param this Current schema
 * @param ref Reference fieldname
 * @param msg Optional error message
 */
export function equalTo(this: yup.MixedSchema, ref: yup.Ref, msg?: string) {
    function _test(this: any, value: any) {
        return value === this.resolve(ref);
    }

    return this.test({
        name: "equalTo",
        exclusive: false,
        message: msg || "${path} and ${reference} fields do not match",
        params: {
            reference: ref.path,
        },
        test: _test,
    });
}

yup.addMethod(yup.mixed, "equalTo", equalTo);

/**
 * Method that validates string field as a phonenumber
 * @param this Current schema
 * @param ref Reference fieldname
 * @param msg Optional error message
 */
export function phoneNumber(this: yup.StringSchema, msg?: string) {
    const regex = new RegExp(/[^\d\-\s]|(^\-)|(\-$)/g);

    function _test(this: any, value?: string) {
        if (!value)
            return true;
        const matches = regex.exec(value);
        return matches === null || matches === undefined || matches.length === 0;
    }

    return this.test({
        name: "phoneNumber",
        exclusive: false,
        message: msg || "${path} is not a valid phone number. Only digits, dashes and spaces are allowed. Do not include country code.",
        test: _test,
    });
}

yup.addMethod(yup.string, "phoneNumber", phoneNumber);

/**
 * Method that validates maximum timespan (00:00:00) of a string
 * @param this Current schema
 * @param max Maximum timespan
 * @param msg Optional error message
 */
export function timespan_max(this: yup.StringSchema, max: number, msg?: string) {
    const controlValDisplay = moment("00:00", "HH:mm").to(moment("00:00", "HH:mm").add(max, "seconds"), true);

    function _test(this: any, value?: string) {
        if (!value)
            return true;

        let m = moment(value, "DD.HH:mm:ss");
        if (!m.isValid())
            m = moment(value, "HH:mm:ss");
        if (!m.isValid())
            m = moment(value, "mm:ss");
        if (!m.isValid())
            return false;

        const a = moment("00:00", "HH:mm");
        const diff = m.diff(a, "seconds", true);
        if (diff > max)
            return false;
        return true;
    }

    return this.test({
        name: "timespan_max",
        exclusive: false,
        message: msg || ("${path} must " + controlValDisplay + " maximum"),
        test: _test,
    });
}

yup.addMethod(yup.string, "timespan_max", timespan_max);

/**
 * Method that validates minimum timespan (00:00:00) of a string
 * @param this Current schema
 * @param min Minimum timespan
 * @param msg Optional error message
 */
export function timespan_min(this: yup.StringSchema, min: number, msg?: string) {
    const controlValDisplay = moment("00:00", "HH:mm").to(moment("00:00", "HH:mm").add(min, "seconds"), true);

    function _test(this: any, value?: string) {
        if (!value)
            return true;

        let m = moment(value, "DD.HH:mm:ss");
        if (!m.isValid())
            m = moment(value, "HH:mm:ss");
        if (!m.isValid())
            m = moment(value, "mm:ss");
        if (!m.isValid())
            return false;

        const a = moment("00:00", "HH:mm");
        const diff = m.diff(a, "seconds", true);
        if (diff < min)
            return false;
        return true;
    }

    return this.test({
        name: "timespan_min",
        exclusive: false,
        message: msg || ("${path} must be " + controlValDisplay + " minimum"),
        test: _test,
    });
}

yup.addMethod(yup.string, "timespan_min", timespan_min);

/**
 * Method that validates maximum word count of a string
 * @param this Current schema
 * @param count Maximum word count
 * @param msg Optional error message
 */
export function wordcount_max(this: yup.StringSchema, count: number, msg?: string) {
    const regex = new RegExp(/[\S]+/gm);

    function _test(this: any, value?: string) {
        const text = Utility.decodeHtml(value);
        const matches = ((text || "").match(regex) || []);
        if (matches.length > count)
            return false;
        return true;
    }

    return this.test({
        name: "wordcount_max",
        exclusive: false,
        message: msg || ("${path} must be at most " + count + " word" + (count === 1 ? "" : "s") + " long"),
        test: _test,
    });
}

yup.addMethod(yup.string, "wordcount_max", wordcount_max);

/**
 * Method that validates minimum word count of a string
 * @param this Current schema
 * @param count Minimum word count
 * @param msg Optional error message
 */
export function wordcount_min(this: yup.StringSchema, count: number, msg?: string) {
    const regex = new RegExp(/[\S]+/gm);

    function _test(this: any, value?: string) {
        const text = Utility.decodeHtml(value);
        const matches = ((text || "").match(regex) || []);
        if (matches.length < count)
            return false;
        return true;
    }

    return this.test({
        name: "wordcount_min",
        exclusive: false,
        message: msg || ("${path} must be at least " + count + " word" + (count === 1 ? "" : "s") + " long"),
        test: _test,
    });
}

yup.addMethod(yup.string, "wordcount_min", wordcount_min);

/**
 * Method that validates maximum character count of a richtext (html) string value
 * @param this Current schema
 * @param count Maximum character count
 * @param msg Optional error message
 */
export function richtextchar_max(this: yup.StringSchema, count: number, msg?: string) {
    function _test(this: any, value?: string) {
        const text = Utility.decodeHtml(value);
        if (text.length > count)
            return false;
        return true;
    }

    return this.test({
        name: "richtextchar_max",
        exclusive: false,
        message: msg || ("${path} must be at most " + count + " character" + (count === 1 ? "" : "s") + " long"),
        test: _test,
    });
}

yup.addMethod(yup.string, "richtextchar_max", richtextchar_max);

/**
 * Method that validates minimum character count of a richtext (html) string value
 * @param this Current schema
 * @param count Minimum character count
 * @param msg Optional error message
 */
export function richtextchar_min(this: yup.StringSchema, count: number, msg?: string) {
    function _test(this: any, value?: string) {
        const text = Utility.decodeHtml(value);
        if (text.length < count)
            return false;
        return true;
    }

    return this.test({
        name: "richtextchar_min",
        exclusive: false,
        message: msg || ("${path} must be at least " + count + " character" + (count === 1 ? "" : "s") + " long"),
        test: _test,
    });
}

yup.addMethod(yup.string, "richtextchar_min", richtextchar_min);

/**
 * Returns the label of the field in the yup validation schema.
 * @param schema The yup schema
 * @param path The path to the schema
 */
export const getFieldLabel = (schema: yup.Schema<any>, path: string) => {
    const nicePath = sentenceCase(path.replace(/.*(\[\d+\]\.)/, ""));
    try {
        const fieldSchema = yup.reach(schema, path);
        const label = (fieldSchema as any)["_label"] as string | undefined;
        return label || nicePath;
    }
    catch (err) {
        return nicePath;
    }
};

/**
 * Method that validates a required React-Select option
 * @param this Current schema
 * @param msg Optional error message
 */
export function requiredReactSelect(this: yup.StringSchema, msg?: string) {
    function _test(this: any, value?: ReactSelectOption) {
        if (!value || value.value === undefined || value.value === null || (typeof value.value === "string" && !value.value))
            return false;
        return true;
    }

    return this.test({
        name: "requiredReactSelect",
        exclusive: false,
        message: msg || ("${path} is required"),
        test: _test,
    });
}

yup.addMethod(yup.object, "requiredReactSelect", requiredReactSelect);



// WEBPACK FOOTER //
// ./src/utils/YupHelper.ts